/**
 * The A/B transport of the voice comparison: one transport over two clips. Which side is in it,
 * whether it is meant to sound, where each side stands and how much of each was heard in this
 * visit. Each side keeps its own place: switching goes on with the other side where it was left,
 * and a side left at its end starts over. The end of a clip stops the transport. Pure:
 * `useVoiceAb` drives two audio elements by it, and stories run it without sound.
 */
import type { PlaybackState } from '@/components/humint/questioning/questioning-types'
import type { RecordingAudioState } from '@/components/humint/questioning/recording-types'
import type { VoicePairSideId } from '@/components/humint/voice/match/voice-pair-types'

/** Speech of one side that has to sound in a visit before the side counts as listened to. */
export const AB_HEARD_MS = 3_000

/** How close to its length a side stands "at its end": an element reports four times a second. */
const END_SLACK_MS = 250

/** The longest step between two readouts that still is playback, not a jump of the playhead. */
const MAX_STEP_MS = 1_500

/** One side of the transport. */
export interface AbSide {
  positionMs: number
  /** The clip's length as far as it is known: the read's figure until the element knows its own. */
  durationMs: number
  /** How much of the side sounded in this visit. */
  heardMs: number
}

export interface AbState {
  active: VoicePairSideId
  /** The transport is meant to sound: play was asked for and nothing has stopped it. */
  playing: boolean
  sides: Record<VoicePairSideId, AbSide>
}

export type AbEvent =
  /** Put a side in the transport. */
  | { type: 'select'; side: VoicePairSideId }
  /** Play or pause the side in the transport. */
  | { type: 'toggle' }
  /** Move a side's playhead; whether it sounds does not change. */
  | { type: 'seek'; side: VoicePairSideId; ms: number }
  /** What a side's element reports of itself. */
  | { type: 'progress'; side: VoicePairSideId; positionMs: number; durationMs: number }
  /** A side's element stopped by itself: its clip ended, or it never started. */
  | { type: 'stopped'; side: VoicePairSideId }

const side = (lengthMs: number | null | undefined): AbSide => ({
  positionMs: 0,
  durationMs: Math.max(0, lengthMs ?? 0),
  heardMs: 0,
})

/** A transport nothing was played in: both sides at their start, the given one selected. */
export function abInitial(
  lengths: Record<VoicePairSideId, number | null | undefined>,
  active: VoicePairSideId = 'a'
): AbState {
  return { active, playing: false, sides: { a: side(lengths.a), b: side(lengths.b) } }
}

const atEnd = ({ positionMs, durationMs }: AbSide): boolean =>
  durationMs > 0 && positionMs >= durationMs - END_SLACK_MS

/** Whether a side is the one that sounds. */
export const abSounding = (state: AbState, id: VoicePairSideId): boolean =>
  state.playing && state.active === id

/** Whether a side was listened to in this visit. */
export const abHeard = (state: AbState, id: VoicePairSideId): boolean =>
  state.sides[id].heardMs >= AB_HEARD_MS

/** A side as a player shows it. */
export function abPlayback(state: AbState, id: VoicePairSideId): PlaybackState {
  const { positionMs, durationMs } = state.sides[id]
  return { positionMs, durationMs, playing: abSounding(state, id) }
}

const withSide = (state: AbState, id: VoicePairSideId, next: AbSide): AbState => ({
  ...state,
  sides: { ...state.sides, [id]: next },
})

/** A side about to sound: one left at its end starts over, or it would end at once. */
function rewound(state: AbState): AbState {
  const current = state.sides[state.active]
  if (!state.playing || !atEnd(current)) return state
  return withSide(state, state.active, { ...current, positionMs: 0 })
}

function progressed(state: AbState, event: Extract<AbEvent, { type: 'progress' }>): AbState {
  const before = state.sides[event.side]
  const positionMs = Math.max(0, event.positionMs)
  const known = Number.isFinite(event.durationMs) && event.durationMs > 0
  const durationMs = Math.max(positionMs, known ? event.durationMs : before.durationMs)
  const step = positionMs - before.positionMs
  // Only what sounded counts as heard: a jump of the playhead, or a silent side, adds nothing.
  const heard = abSounding(state, event.side) && step > 0 && step <= MAX_STEP_MS
  if (positionMs === before.positionMs && durationMs === before.durationMs) return state
  const heardMs = before.heardMs + (heard ? step : 0)
  return withSide(state, event.side, { positionMs, durationMs, heardMs })
}

function stopped(state: AbState, id: VoicePairSideId): AbState {
  if (!abSounding(state, id)) return state
  const current = state.sides[id]
  // A clip played to its end was heard, however short it is.
  const heardMs = atEnd(current) ? Math.max(current.heardMs, AB_HEARD_MS) : current.heardMs
  return { ...withSide(state, id, { ...current, heardMs }), playing: false }
}

export function abReduce(state: AbState, event: AbEvent): AbState {
  switch (event.type) {
    case 'select':
      if (event.side === state.active) return state
      return rewound({ ...state, active: event.side })
    case 'toggle':
      return rewound({ ...state, playing: !state.playing })
    case 'seek': {
      const current = state.sides[event.side]
      const positionMs = Math.min(Math.max(0, event.ms), current.durationMs)
      return withSide(state, event.side, { ...current, positionMs })
    }
    case 'progress':
      return progressed(state, event)
    case 'stopped':
      return stopped(state, event.side)
  }
}

// ── Driving an element ───────────────────────────────────────────────────────

/**
 * A command sent to a side's element that its readout has not shown yet, or `owed`: a play that
 * waits for the side's clip to arrive.
 */
export type AbFlight = 'play' | 'pause' | 'owed' | null

/** One side as its driver sees it. */
export interface AbWatch {
  /** The transport wants this side to sound. */
  wanted: boolean
  /** How its clip stands. */
  audio: RecordingAudioState
  /** The element reports that it plays. */
  playing: boolean
  flight: AbFlight
}

/**
 * What to ask of a side's element next: `play` and `pause` are for the element, `stopped` tells
 * the transport that the element stopped by itself.
 */
export type AbOrder = 'play' | 'pause' | 'stopped' | null

/**
 * The next order for one side's element, from what the transport wants and what the element
 * reports. An element is only ever asked to pause when it says it plays and no command is in
 * flight, because its one control is a toggle: asking twice would start it again. A side that
 * cannot be played, or that fell silent while it was meant to sound, stops the transport.
 */
export function abOrder({ wanted, audio, playing, flight }: AbWatch): {
  order: AbOrder
  flight: AbFlight
} {
  if (wanted && audio === 'unavailable') return { order: 'stopped', flight: null }
  // A command in flight is over once the element shows it.
  const shown = (flight === 'play' && playing) || (flight === 'pause' && !playing)
  const pending = shown ? null : flight
  if (pending === 'owed') {
    if (!wanted) return { order: null, flight: null }
    return audio === 'ready' ? { order: 'play', flight: 'play' } : { order: null, flight: 'owed' }
  }
  if (pending) return { order: null, flight: pending }
  if (playing && !wanted) return { order: 'pause', flight: 'pause' }
  if (!playing && wanted) return { order: 'stopped', flight: null }
  return { order: null, flight: null }
}
